import { Component, Input, OnInit } from '@angular/core';

import { MessageService } from 'primeng/api';
import moment from 'moment-timezone';

import { AbstractDialogComponent } from '../../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { messageSeverityEnum } from '../../../../../Common/Misc/enums/message-severity.enum';
import { ButtonTypesEnum } from '../../../../../Common/Forms/enums/button-types.enum';
import { TargetingResultModel } from '../../../models/targeting-result.model';
import { TargetingMiscApi } from '../../../../../api/targeting/targeting-misc.api';
import { DropdownOptionModel } from '../../../../../Common/Forms/models/dropdown-option.model';
import { getAllDateQueryOptions } from '../../../../../Monitor/Shared/models/monitoring-date-query-input-options';
import { CsvInteractionExportField, csvInteractionFieldOrder } from './csv-interaction-export-field.enum';
import { CsvInteractionExportType } from './csv-interaction-export-type.enum';
import { SourcePickerService } from '../../../services/source-picker-service';
import { TargetingSearchService } from '../../../services/targeting-search.service';
import { InteractionsExportApi } from '../../../../../api/targeting/model/interactions-export.api';
import { download } from '../../../../../Common/Misc/helpers/download/download.helper';
import { catchError, of } from 'rxjs';
import { CsvInteractionExportFieldInterface } from './csv-interaction-export-field.interface';
import { DateQueryModel } from '../../../../../Common/Forms/models/date-query.model';
import { DateQueryTypeEnum } from '../../../../../api/mentions/enum/date-query-type.enum';
import { sortArrayByPredefinedEnumOrder } from '../../../../../Common/Misc/helpers/sort/sort.helper';

@Component({
    selector: 'agpr-csv-interaction-export-dialog',
    templateUrl: './csv-interaction-export-dialog.component.html',
    styleUrls: ['./csv-interaction-export-dialog.component.scss'],
    standalone: false
})
export class CsvInteractionExportDialogComponent extends AbstractDialogComponent implements OnInit {

    readonly messageSeverities = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;

    @Input() selectedEntities: TargetingResultModel[];
    @Input() allSelected: boolean;
    @Input() isAllInteractions?: boolean;
    @Input() sourceName: string = '';

    contactCount: number = 0;
    outletCount: number = 0;
    exportTooltip: string = '';
    isExportValidate: boolean = false;

    loading: boolean = false;
    id = 'CsvInteractionExportDialog';

    includedInteractionTypes = [];
    includedInteractionData: CsvInteractionExportFieldInterface[] = [];
    selectedTypeFields = [];
    selectedDataFields = [];
    dateFilterOptions: DropdownOptionModel[] = getAllDateQueryOptions();
    dateQuery = this.dateFilterOptions[0].value.query;
    filename: string = '';

    constructor(
        public coreDialogService: CoreDialogService,
        public targetingMiscApi: TargetingMiscApi,
        public sourcePickerService: SourcePickerService,
        public targetingSearchService: TargetingSearchService,
        public messageService: MessageService,

    ) {
        super(coreDialogService, 'CsvInteractionExport');
    }

    ngOnInit() {
        this.filename = 'Interaction_' + moment().format('YYYYMMDD');

        if (this.sourceName) {
            this.filename = 'Interaction_' + this.sourceName + '_' + moment().format('YYYYMMDD');
        }

        this.includedInteractionTypes = [
            {
                enumValue: CsvInteractionExportType.EMAIL,
                label: 'Discover.batchActions.interactionExport.field.email'
            },
            {
                enumValue: CsvInteractionExportType.DISTRIBUTION,
                label: 'Discover.batchActions.interactionExport.field.distributions'
            },
            {
                enumValue: CsvInteractionExportType.PHONE,
                label: 'Discover.batchActions.interactionExport.field.phone'
            },
            {
                enumValue: CsvInteractionExportType.DIRECT_MESSAGE,
                label: 'Discover.batchActions.interactionExport.field.directMessage'
            },
            {
                enumValue: CsvInteractionExportType.INBOUND_INQUIRY,
                label: 'Discover.batchActions.interactionExport.field.inboundInquiry'
            },
            {
                enumValue: CsvInteractionExportType.OTHER,
                label: 'Discover.batchActions.interactionExport.field.other'
            }
        ];

        this.includedInteractionData = [
            {
                enumValue: CsvInteractionExportField.CONTACT_NAME,
                label: 'Discover.batchActions.interactionExport.field.contactName',
            },
            {
                enumValue: CsvInteractionExportField.INTERACTION_TIME,
                label: 'Discover.batchActions.interactionExport.field.interactionTime'
            },
            {
                enumValue: CsvInteractionExportField.OUTLET_NAME,
                label: 'Discover.batchActions.interactionExport.field.outletName',
            },
            {
                enumValue: CsvInteractionExportField.INTERACTION_SUMMARY,
                label: 'Discover.batchActions.interactionExport.field.interactionSummary'
            },
            {
                enumValue: CsvInteractionExportField.INTERACTION_TYPE,
                label: 'Discover.batchActions.interactionExport.field.interactionType'
            },
            {
                enumValue: CsvInteractionExportField.INTERACTION_NOTES,
                label: 'Discover.batchActions.interactionExport.field.interactionNotesResponse'
            },
            {
                enumValue: CsvInteractionExportField.INTERACTION_DATE,
                label: 'Discover.batchActions.interactionExport.field.interactionDate'
            },
            {
                enumValue: CsvInteractionExportField.USERNAME,
                label: 'Discover.batchActions.interactionExport.field.userName'
            }
        ];

        this.selectedTypeFields = [];
        this.includedInteractionTypes.forEach(x => {
            this.selectedTypeFields.push(x);
        });

        this.selectedDataFields = [];
        this.includedInteractionData.forEach(x => {
            this.selectedDataFields.push(x);
        });

        this.getEntityCount();
    }


    ok(): void {
        this.loading = true;
        const request = this.constructRequestModel();

        this.targetingMiscApi.exportInteractions(request).pipe(catchError(
            () => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    detail: 'Discover.csvExport.error',
                });
                this.loading = false;
                this.closeDialog(false);
                return of(null);
            }
        )).subscribe( csvString => {
            if (csvString) {
                download.blob(download.getCSVBlob(csvString), this.filename + '.csv');
                this.loading = false;
                this.closeDialog(true);
            }
        });
    }

    constructRequestModel(): InteractionsExportApi {
        const types = this.selectedTypeFields.map(x => x.enumValue);
        const fields = this.selectedDataFields.map(x => x.enumValue);
        sortArrayByPredefinedEnumOrder(fields, csvInteractionFieldOrder);

        let dateRange;
        if (this.dateQuery.type === DateQueryTypeEnum.ALL) {
            dateRange = {
                createdFrom: null,
                createdTo: null,
            };
        } else {
            const range = DateQueryModel.makeAbsoluteRange(this.dateQuery);
            dateRange = {
                createdFrom: moment(range.absoluteRange.start).format(),
                createdTo: moment(range.absoluteRange.end).format(),
            };
        }

        const request: InteractionsExportApi = {
            dateRange,
            types,
            fields
        };

        if (this.selectedEntities) {
            request.entityIds = this.selectedEntities.map(x => x.toEntityIdModel());
        } else if (this.isAllInteractions) {
            request.isAllInteractions = true;
            request.query = this.sourcePickerService.getCurrentSearchContext();
        } else {
            if (this.allSelected) {
                request.query = this.targetingSearchService.getCurrentSearchContext();
            } else {
                request.query = this.sourcePickerService.getCurrentSearchContext();
            }
        }

        return request;
    }

    getEntityCount(): void {
        this.loading = true;
        const request = this.constructRequestModel();
        this.targetingMiscApi.getEntityCountForInteractionExport(request).subscribe(entityCount => {
            this.loading = false;
            this.contactCount = entityCount.contactCount;
            this.outletCount = entityCount.outletCount;
            this.validate();
        });
    }

    validate(): void {
        if (!this.filename?.trim().length) {
            this.exportTooltip = 'Discover.batchActions.interactionExport.invalid.filename';
        } else if(!this.selectedTypeFields.length) {
            this.exportTooltip = 'Discover.batchActions.interactionExport.invalid.type';
        } else if(!this.selectedDataFields.length) {
            this.exportTooltip = 'Discover.batchActions.interactionExport.invalid.data';
        } else if(!this.contactCount && !this.outletCount) {
            this.exportTooltip = 'Discover.batchActions.interactionExport.invalid.count';
        } else {
            this.exportTooltip = '';
        }

        this.isExportValidate = !!this.filename?.trim().length
            && !!this.selectedTypeFields.length
            && !!this.selectedDataFields.length
            && (!!this.contactCount || !!this.outletCount);
    }

    typeChange(): void {
        if (this.selectedTypeFields.length) {
            this.getEntityCount();
        } else {
            this.contactCount = 0;
            this.outletCount = 0;
            this.validate();
        }
    }

    cancel(): void {
        this.closeDialog(false);
    }
}
